import { useState, useEffect } from "react";
import { 
  FileText, 
  MessageSquare, 
  Star, 
  Mail,
  Plus,
  Edit,
  Trash2,
  Check,
  X,
  Eye,
  EyeOff,
  CalendarCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [newPost, setNewPost] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category: "Tax Tips",
    published: true
  });

  const authHeader = {
    auth: {
      username: credentials.username,
      password: credentials.password
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.get(`${API}/admin/stats`, authHeader);
      setIsAuthenticated(true);
      toast.success("Logged in successfully");
      loadData();
    } catch (error) {
      toast.error("Invalid credentials");
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, postsRes, contactsRes, testimonialsRes, inquiriesRes, bookingsRes, leadsRes, settingsRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeader),
        axios.get(`${API}/posts?published_only=false`),
        axios.get(`${API}/contacts`, authHeader),
        axios.get(`${API}/testimonials?approved_only=false`),
        axios.get(`${API}/bookkeeping-inquiries`, authHeader),
        axios.get(`${API}/bookings`, authHeader),
        axios.get(`${API}/leads`, authHeader),
        axios.get(`${API}/admin/settings`, authHeader),
        axios.get(`${API}/payments`, authHeader)
      ]);
      setStats(statsRes.data);
      setPosts(postsRes.data);
      setContacts(contactsRes.data);
      setTestimonials(testimonialsRes.data);
      setInquiries(inquiriesRes.data);
      setBookings(bookingsRes.data);
      setLeads(leadsRes.data);
      setSettings(settingsRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const seedData = async () => {
    try {
      await axios.post(`${API}/admin/seed`, {}, authHeader);
      toast.success("Sample data seeded");
      loadData();
    } catch (error) {
      toast.error("Failed to seed data");
    }
  };

  const createPost = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/posts`, newPost, authHeader);
      toast.success("Post created");
      setNewPost({ title: "", slug: "", excerpt: "", content: "", category: "Tax Tips", published: true });
      loadData();
    } catch (error) {
      toast.error("Failed to create post");
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await axios.delete(`${API}/admin/posts/${postId}`, authHeader);
      toast.success("Post deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const approveTestimonial = async (testimonialId) => {
    try {
      await axios.put(`${API}/admin/testimonials/${testimonialId}/approve`, {}, authHeader);
      toast.success("Testimonial approved");
      loadData();
    } catch (error) {
      toast.error("Failed to approve testimonial");
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status?status=${status}`, {}, authHeader);
      toast.success(`Booking marked ${status}`);
      loadData();
    } catch (error) {
      toast.error("Failed to update booking");
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, authHeader);
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const setSetting = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-bone py-20">
        <div className="max-w-md mx-auto px-4">
          <Card className="bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="font-heading text-2xl text-navy text-center">
                Admin Login
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={credentials.username}
                    onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                    data-testid="admin-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={credentials.password}
                    onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                    data-testid="admin-password-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-navy hover:bg-gray-800"
                  data-testid="admin-login-btn"
                >
                  Login
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-page">
      {/* Header */}
      <div className="bg-navy text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <h1 className="font-heading text-2xl font-bold">Admin Dashboard</h1>
            <Button
              variant="outline"
              className="text-white border-white hover:bg-white hover:text-navy"
              onClick={() => setIsAuthenticated(false)}
              data-testid="admin-logout-btn"
            >
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {["dashboard", "bookings", "leads", "payments", "posts", "contacts", "testimonials", "inquiries", "settings"].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? "default" : "outline"}
              onClick={() => setActiveTab(tab)}
              className="capitalize"
              data-testid={`admin-tab-${tab}`}
            >
              {tab}
            </Button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-2xl font-bold text-navy">Overview</h2>
              <Button onClick={seedData} variant="outline" data-testid="admin-seed-btn">
                Seed Sample Data
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-bronze/20 flex items-center justify-center">
                      <CalendarCheck className="w-6 h-6 text-bronze" />
                    </div>
                    <div>
                      <p className="font-body text-sm text-gray-500">Appointment Requests</p>
                      <p className="font-heading text-2xl font-bold text-navy">{stats?.bookings || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-bronze/20 flex items-center justify-center">
                      <Mail className="w-6 h-6 text-bronze" />
                    </div>
                    <div>
                      <p className="font-body text-sm text-gray-500">Leads</p>
                      <p className="font-heading text-2xl font-bold text-navy">{stats?.leads || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-bronze/20 flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-bronze" />
                    </div>
                    <div>
                      <p className="font-body text-sm text-gray-500">Payments (Paid)</p>
                      <p className="font-heading text-2xl font-bold text-navy">{stats?.payments || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-body text-sm text-gray-500">Contacts</p>
                      <p className="font-heading text-2xl font-bold text-navy">{stats?.contacts || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-body text-sm text-gray-500">Blog Posts</p>
                      <p className="font-heading text-2xl font-bold text-navy">{stats?.posts || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                      <Star className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-body text-sm text-gray-500">Testimonials</p>
                      <p className="font-heading text-2xl font-bold text-navy">{stats?.testimonials || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-body text-sm text-gray-500">Bookkeeping Inquiries</p>
                      <p className="font-heading text-2xl font-bold text-navy">{stats?.bookkeeping_inquiries || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl font-bold text-navy">Appointment Requests</h2>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Preferred</TableHead>
                    <TableHead>Alternate</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                        No appointment requests yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {bookings.map((b) => (
                    <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                      <TableCell className="font-medium">{b.name}</TableCell>
                      <TableCell>
                        <div className="text-sm">{b.email}</div>
                        <div className="text-xs text-gray-500">{b.phone || "-"}</div>
                      </TableCell>
                      <TableCell>{b.appointment_type}</TableCell>
                      <TableCell>{b.preferred_date} @ {b.preferred_time}</TableCell>
                      <TableCell>{b.alternate_date ? `${b.alternate_date} @ ${b.alternate_time}` : "-"}</TableCell>
                      <TableCell>
                        <span className={
                          b.status === "confirmed" ? "text-green-600" :
                          b.status === "cancelled" ? "text-red-600" : "text-yellow-600"
                        }>
                          {b.status === "time_proposed" ? "time proposed" : b.status}
                        </span>
                        {b.proposed_date && (
                          <div className="text-xs text-gray-500 mt-1">
                            proposed: {b.proposed_date} @ {b.proposed_time}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {b.status !== "confirmed" && (
                            <Button variant="outline" size="sm" onClick={() => updateBookingStatus(b.id, "confirmed")} data-testid={`booking-confirm-${b.id}`}>
                              Confirm
                            </Button>
                          )}
                          {b.status !== "cancelled" && (
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => updateBookingStatus(b.id, "cancelled")} data-testid={`booking-cancel-${b.id}`}>
                              Cancel
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === "payments" && (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl font-bold text-navy">Payments</h2>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>For</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-8">No payments yet.</TableCell></TableRow>
                  )}
                  {payments.map((p) => (
                    <TableRow key={p.session_id} data-testid={`payment-row-${p.session_id}`}>
                      <TableCell className="text-sm">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "-"}</TableCell>
                      <TableCell className="font-medium">{p.name || "-"}</TableCell>
                      <TableCell className="text-sm">{p.email || "-"}</TableCell>
                      <TableCell className="text-sm">{p.phone || "-"}</TableCell>
                      <TableCell className="text-sm">{p.label || "-"}</TableCell>
                      <TableCell className="font-semibold">${Number(p.amount || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <span className={
                          p.payment_status === "paid" ? "text-green-600 font-medium" :
                          p.payment_status === "failed" ? "text-red-600" : "text-yellow-600"
                        }>
                          {p.payment_status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === "leads" && (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl font-bold text-navy">Leads</h2>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-8">No leads yet.</TableCell></TableRow>
                  )}
                  {leads.map((l) => (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                      <TableCell><span className="text-xs font-mono bg-bone px-2 py-1 rounded">{l.form_type}</span></TableCell>
                      <TableCell className="font-medium">{l.name}</TableCell>
                      <TableCell>
                        <div className="text-sm">{l.email}</div>
                        <div className="text-xs text-gray-500">{l.phone || "-"}</div>
                      </TableCell>
                      <TableCell>{l.service_category || "-"}</TableCell>
                      <TableCell>{l.lead_source || "-"}</TableCell>
                      <TableCell><span className="text-yellow-600">{l.status}</span></TableCell>
                      <TableCell className="max-w-[220px]">
                        <div className="text-xs text-gray-500 truncate">{Object.entries(l.payload || {}).map(([k, v]) => `${k}: ${v}`).join(", ") || "-"}</div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && settings && (
          <div className="space-y-6 max-w-2xl">
            <div>
              <h2 className="font-heading text-2xl font-bold text-navy">Site Settings</h2>
              <p className="font-body text-sm text-gray-500">Edit these anytime — changes go live across the site immediately.</p>
            </div>
            <form onSubmit={saveSettings} className="space-y-5">
              {[
                { key: "portal_url", label: "MyTAXPrepOffice Portal URL", placeholder: "https://..." },
                { key: "booking_url", label: "Booking URL (leave blank to use built-in booking modal)", placeholder: "https://calendly.com/..." },
                { key: "google_review_url", label: "Google Review URL", placeholder: "https://g.page/..." },
                { key: "business_email", label: "Business Email", placeholder: "INFO@DATAXGUY.COM" },
                { key: "business_phone", label: "Business Phone", placeholder: "201-253-7674" },
                { key: "form_notification_email", label: "Form Notification Email (where new leads are sent)", placeholder: "INFO@DATAXGUY.COM" },
                { key: "webhook_url", label: "Automation Webhook URL (Zapier/Make/n8n — optional)", placeholder: "https://hooks.zapier.com/..." },
                { key: "facebook_url", label: "Facebook URL", placeholder: "https://facebook.com/..." },
                { key: "instagram_url", label: "Instagram URL", placeholder: "https://instagram.com/..." },
                { key: "linkedin_url", label: "LinkedIn URL", placeholder: "https://linkedin.com/..." },
                { key: "twitter_url", label: "Twitter/X URL", placeholder: "https://x.com/..." },
              ].map((f) => (
                <div key={f.key}>
                  <Label htmlFor={`set-${f.key}`}>{f.label}</Label>
                  <Input
                    id={`set-${f.key}`}
                    data-testid={`settings-${f.key}`}
                    value={settings[f.key] || ""}
                    onChange={(e) => setSetting(f.key, e.target.value)}
                    placeholder={f.placeholder}
                  />
                </div>
              ))}
              <Button type="submit" disabled={savingSettings} className="bg-navy text-white rounded-full px-8" data-testid="settings-save-btn">
                {savingSettings ? "Saving..." : "Save Settings"}
              </Button>
            </form>
          </div>
        )}

        {/* Posts Tab */}
        {activeTab === "posts" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-2xl font-bold text-navy">Blog Posts</h2>
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-navy hover:bg-gray-800" data-testid="admin-new-post-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Post
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Post</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={createPost} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input
                        value={newPost.title}
                        onChange={(e) => {
                          setNewPost({
                            ...newPost, 
                            title: e.target.value,
                            slug: generateSlug(e.target.value)
                          });
                        }}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Slug</Label>
                      <Input
                        value={newPost.slug}
                        onChange={(e) => setNewPost({...newPost, slug: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Input
                        value={newPost.category}
                        onChange={(e) => setNewPost({...newPost, category: e.target.value})}
                        placeholder="Tax Tips, Business, etc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Excerpt</Label>
                      <Textarea
                        value={newPost.excerpt}
                        onChange={(e) => setNewPost({...newPost, excerpt: e.target.value})}
                        rows={2}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Content (HTML)</Label>
                      <Textarea
                        value={newPost.content}
                        onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                        rows={10}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-navy hover:bg-gray-800">
                      Create Post
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posts.map((post) => (
                    <TableRow key={post.id}>
                      <TableCell className="font-medium">{post.title}</TableCell>
                      <TableCell>{post.category}</TableCell>
                      <TableCell>
                        {post.published ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <Eye className="w-4 h-4" /> Published
                          </span>
                        ) : (
                          <span className="text-gray-500 flex items-center gap-1">
                            <EyeOff className="w-4 h-4" /> Draft
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deletePost(post.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl font-bold text-navy">Contact Submissions</h2>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow key={contact.id}>
                      <TableCell className="font-medium">{contact.name}</TableCell>
                      <TableCell>{contact.email}</TableCell>
                      <TableCell>{contact.phone || "-"}</TableCell>
                      <TableCell>{contact.service_needed}</TableCell>
                      <TableCell className="max-w-xs truncate">{contact.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}

        {/* Testimonials Tab */}
        {activeTab === "testimonials" && (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl font-bold text-navy">Testimonials</h2>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Content</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {testimonials.map((testimonial) => (
                    <TableRow key={testimonial.id}>
                      <TableCell className="font-medium">{testimonial.name}</TableCell>
                      <TableCell>{testimonial.role}</TableCell>
                      <TableCell className="max-w-xs truncate">{testimonial.content}</TableCell>
                      <TableCell>
                        {testimonial.approved ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <Check className="w-4 h-4" /> Approved
                          </span>
                        ) : (
                          <span className="text-yellow-600 flex items-center gap-1">
                            <X className="w-4 h-4" /> Pending
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {!testimonial.approved && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => approveTestimonial(testimonial.id)}
                          >
                            Approve
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}

        {/* Inquiries Tab */}
        {activeTab === "inquiries" && (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl font-bold text-navy">Bookkeeping Inquiries</h2>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>Package</TableHead>
                    <TableHead>Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inquiries.map((inquiry) => (
                    <TableRow key={inquiry.id}>
                      <TableCell className="font-medium">{inquiry.name}</TableCell>
                      <TableCell>{inquiry.email}</TableCell>
                      <TableCell>{inquiry.business_name || "-"}</TableCell>
                      <TableCell className="capitalize">{inquiry.package_interest}</TableCell>
                      <TableCell className="max-w-xs truncate">{inquiry.message || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
